<template>
  <UiTeleport to="#pb-dialogs">
    <BaseDialog
      :class-name="className"
      :loading="loading"
      :size="size ? `control-dialog-${size}` : ''"
      :show-close="showClose"
      :state="active"
      :horizontal-scroll="horizontalScroll"
      :vertical-scroll="verticalScroll"
      :with-style="withStyle"
      :close-to="closeTo"
      :disable-close="loading"
      :transition="transition"
      @open="open"
      @close="close"
    >
      <template #title>
        <slot name="title">
          {{ dialogTitle }}
        </slot>
      </template>

      <template
        v-if="hasAfterTitleSlot"
        #afterTitle
      >
        <slot name="afterTitle" />
      </template>

      <slot />

      <template
        v-if="hasFooterSlot"
        #footer
      >
        <slot name="footer" />
      </template>
      <!-- @keydown.esc="close" on the overlay -->
    </BaseDialog>
  </UiTeleport>
</template>

<script setup>
import {
  watch,
} from 'vue'

import BaseDialog from '@nicolaudie-group/ng-ui-kit/components/BaseDialog/BaseDialog.vue'
import UiTeleport from '@nicolaudie-group/ng-ui-kit/components/UiTeleport/UiTeleport.vue'
import useStateProp from '@nicolaudie-group/ng-ui-kit/composables/useStateProp'
import useHasContent from '@nicolaudie-group/ng-ui-kit/composables/useHasContent'
import useHotkeys, { KEYS } from '@nicolaudie-group/ng-ui-kit/composables/useHotkeys'

import useDialogStack from '@/composables/useDialogStack'
import useRouterStore from '@/stores/router.store'

defineOptions({
  compatConfig: {
    MODE: 3, // opt-in to Vue 3 behavior for this component only
  },
  inheritAttrs: false,
})

const props = defineProps({
  state: { type: Boolean, default: true },
  dialogTitle: { type: String, default: '' },
  size: { type: String, default: '', enum: ['', 'medium', 'large', 'xlarge'] },
  showClose: { type: Boolean, default: true },
  loading: { type: Boolean, default: false },
  verticalScroll: { type: Boolean, default: true },
  horizontalScroll: { type: Boolean, default: false },
  withStyle: { type: Boolean, default: true },
  className: { type: [Object, Array, String], default: '' },
  closeTo: { type: [String, Object], default: null },
  transition: { type: String, default: null },
  /**
   * For debugging purposes, used in the dialog stack
   */
  name: { type: String, default: undefined },
})

const emit = defineEmits(['open', 'close'])

const { active, close: stateClose, open } = useStateProp({ props, emit })

function close() {
  if (!props.loading) {
    return stateClose()
  }
  return undefined
}

const routerStore = useRouterStore()

const { active: escPressed } = useHotkeys(KEYS.Escape, {
  shift: false,
  alt: false,
  meta: false,
  ctrl: false,
  enabled: active,
})

const isOnTop = useDialogStack(active, { name: props.name || props.dialogTitle })

watch(escPressed, () => {
  if (isOnTop.value && escPressed.value) {
    close()
    if (props.closeTo) {
      routerStore.push(props.closeTo)
    }
  }
})

const hasAfterTitleSlot = useHasContent('afterTitle')
const hasFooterSlot = useHasContent('footer')
</script>
